import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  ThemeProvider,
  Tooltip,
  createTheme,
  FormControl,
  InputLabel,
  Box,
  TextField,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import LocalPrice from "../../../utils/LocalPrice";
import { useState, useCallback, useEffect, useRef } from "react";
import axios from "../../../api/axios";
import { toast } from "react-toastify";
import Errors from "../../../utils/Errors";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  fastListState,
  fileChangeState,
  selectedFilesIntState,
} from "../../../atoms/fastShipment";
import Currency from "../../../utils/CurrencySymbol";
import countries from "../../../json/countries.json";
import iossCountries from "../../../json/iossCountries.json";
import {
  integrationsState,
  newIossNumberAnimationState,
} from "../../../atoms/membership";
import {
  platformCountryState,
  proformaChangeState,
} from "../../../atoms/profileSettings";
import { proformaModalState } from "../../../atoms/createShipment";
import { FormatReturnFieldWithTwoDecimals } from "../../../utils/FormatFieldReturn";
import FormatDateHour from "../../../utils/FormatDateHour";
import GetFile from "../../../utils/GetFile";

const theme = createTheme({
  components: {
    MuiTableCell: { styleOverrides: { root: { padding: "8px" } } },
  },
});

const PackageListItem = ({
  itemID,
  selectedCountryForIoss,
  product,
  selectedFastShipmentProduct,
  setSelectedFastShipmentProduct,
  setOpenPriceUpdateConfirmation,
  setUpdatedCommoduty,
  setOpenPriceUpdate,
  setPriceID,
  setPriceIDValue,
  isEditable,
  setSelectedPriceObject,
  index,
  handleClose,
  handleCheckPrice,
  priceIsDisabled,
  allCurrenciesSame,
  setProforma,
  isLastItem,
  isFirstItem,
  totalPriceAll,
  totalQuantityAll,
  initialTotalPrice,
  setInitialTotalPrice,
  bulkPrice,
  setBulkPrice,
  setTotalPriceCalc,
}) => {
  const [loading, setLoading] = useState(false);
  const [hsCodeError, setHsCodeError] = useState(false);
  const initialHSCode = useRef(product.hs_code);
  const integrations = useRecoilValue(integrationsState);
  const setProformaModal = useSetRecoilState(proformaModalState);
  const setNewIossNumberAnimation = useSetRecoilState(
    newIossNumberAnimationState
  );
  const setPlatformCountry = useSetRecoilState(platformCountryState);
  const setProformaChange = useSetRecoilState(proformaChangeState);
  const [fileChange, setFileChange] = useRecoilState(fileChangeState);
  const IOSSCountry =
    iossCountries.find((country) => country.iso_code === itemID?.country) ||
    iossCountries.find(
      (country) => country.iso_code === selectedCountryForIoss
    );

  const [sendFiles] = useRecoilState(selectedFilesIntState);
  const [fastList, setFastList] = useRecoilState(fastListState);
  const member = JSON.parse(localStorage.getItem("user"));
  const memberId = member?.user?.user_member?.id;

  const [bulkParcelType, setBulkParcelType] = useState("");
  const [bulkCurrency, setBulkCurrency] = useState("");
  const [bulkCountry, setBulkCountry] = useState("");
  const [bulkTitle, setBulkTitle] = useState("");
  const [bulkSku, setBulkSku] = useState("");
  const [bulkHsCode, setBulkHsCode] = useState("");
  const [bulkQuantity, setBulkQuantity] = useState("");
  const [bulkIoss, setBulkIoss] = useState("");

  // HS Code arama için state'ler
  const [hsOptions, setHsOptions] = useState([]);
  const [selectedHsOption, setSelectedHsOption] = useState(null);
  const [hsCodeSearch, setHsCodeSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [ddpRate, setDdpRate] = useState(null);
  const [initialPrice, setInitialPrice] = useState(product.price);

  // Member HS codes için state'ler
  const [memberHsCodes, setMemberHsCodes] = useState([]);
  const [memberHsLoading, setMemberHsLoading] = useState(false);
  const [selectedMemberHsCode, setSelectedMemberHsCode] = useState("");
  const [isMemberHsCodeSelected, setIsMemberHsCodeSelected] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [taxLoading, setTaxLoading] = useState(false);

  // Bulk HS Code arama için state'ler
  const [bulkHsOptions, setBulkHsOptions] = useState([]);
  const [bulkSelectedHsOption, setBulkSelectedHsOption] = useState(null);
  const [bulkHsCodeSearch, setBulkHsCodeSearch] = useState("");
  const [bulkSearchLoading, setBulkSearchLoading] = useState(false);
  const [bulkDdpRate, setBulkDdpRate] = useState(null);
  const [bulkSelectedMemberHsCode, setBulkSelectedMemberHsCode] = useState("");
  const [isBulkMemberHsCodeSelected, setIsBulkMemberHsCodeSelected] = useState(false);
  const [bulkAutocompleteOpen, setBulkAutocompleteOpen] = useState(false);
  const [bulkTaxLoading, setBulkTaxLoading] = useState(false);

  // Helper function to calculate total price
  const calculateTotalPrice = useCallback(() => {
    return selectedFastShipmentProduct.reduce((total, product) => {
      return total + Number(product.quantity) * Number(product.price);
    }, 0);
  }, [selectedFastShipmentProduct]);

  // Helper function to check if any product has missing tax rate
  const hasAnyProductWithMissingTaxRate = useCallback(() => {
    // Sadece US ülkesi için gümrük vergisi kontrolü yap
    if (itemID?.country !== "US") {
      return false;
    }

    return selectedFastShipmentProduct.some((product) => {
      // Eğer product.hs_option varsa, onun data'sını kontrol et
      if (product.hs_option && product.hs_option.data) {
        const taxRate =
          product.hs_option.data.general ||
          product.hs_option.data.special ||
          product.hs_option.data.other;
        return taxRate === null || taxRate === undefined || taxRate === "";
      }

      // Eğer product.ddp_rate varsa, onu kontrol et
      if (product.ddp_rate === null || product.ddp_rate === undefined) {
        return true;
      }

      // Eğer HS kodu varsa ama gümrük vergisi bilgisi yoksa
      if (
        product.hs_code &&
        product.hs_code.length >= 8 &&
        !product.hs_option
      ) {
        return true;
      }

      return false;
    });
  }, [selectedFastShipmentProduct, itemID?.country]);

  useEffect(() => {
    // Calculate and set the initial total price when the component mounts
    setInitialTotalPrice(calculateTotalPrice());
  }, [setInitialTotalPrice, calculateTotalPrice]);

  // Member HS codes'ları getir
  const fetchMemberHsCodes = useCallback(async () => {
    setMemberHsLoading(true);
    try {
      const response = await axios.get("member-hs-code");
      if (response.data && response.data.status === 200 && response.data.data) {
        const formattedMemberCodes = response.data.data.map((item) => ({
          label: `${item.hs_code} - ${item.description}`,
          value: item.hs_code, // Sadece HS kodu
          data: {
            htsno: item.hs_code,
            description: item.description,
            general: null,
            special: null,
            other: null,
          },
          isMemberCode: true,
          memberId: item.id,
        }));
        setMemberHsCodes(formattedMemberCodes);
      }
    } catch (error) {
      console.error("Member HS codes yükleme hatası:", error);
    } finally {
      setMemberHsLoading(false);
    }
  }, []);

  // Mevcut ürünün HS kodu varsa Autocomplete'te göster
  useEffect(() => {
    // Önce product.hs_option var mı kontrol et (bulk update'ten gelmiş olabilir)
    if (product.hs_option) {
      setSelectedHsOption(product.hs_option);
      setHsOptions([product.hs_option]);
      if (product.ddp_rate !== null && product.ddp_rate !== undefined) {
        setDdpRate(product.ddp_rate);
      }
    } else if (product.hs_code && product.hs_code.length >= 6) {
      // Mevcut HS kodu için basit bir option oluştur
      const existingHsOption = {
        label: `${product.hs_code} - Mevcut HS Kodu`,
        value: product.hs_code,
        data: {
          htsno: product.hs_code,
          description: "Mevcut HS Kodu",
          general: product.ddp_rate ? `${product.ddp_rate}%` : "0%",
          special: null,
          other: null,
        },
      };

      // Eksik değer varsa seçenekleri temizle
      if (product.ddp_rate === null || product.ddp_rate === undefined) {
        setSelectedHsOption(null);
        setHsOptions([]);
        setDdpRate(null);
      } else {
        setSelectedHsOption(existingHsOption);
        setHsOptions([existingHsOption]);
        setDdpRate(product.ddp_rate);
      }
    } else {
      // HS kodu yoksa temizle
      setSelectedHsOption(null);
      setHsOptions([]);
      setDdpRate(null);
    }
  }, [product.hs_code, product.ddp_rate, product.hs_option]);

  // HS Code arama fonksiyonları
  const searchHsCodes = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length <= 2) {
      setHsOptions([]);
      return;
    }

    // Sadece HS kodu kısmını al (açıklama kısmını çıkar)
    const cleanSearchTerm = searchTerm.split(" - ")[0].trim();

    setSearchLoading(true);
    try {
      const response = await axios.get(`hscode-search/${cleanSearchTerm}`);

      if (response.data && !response.data.error) {
        let dataToProcess = null;

        if (response.data.items && Array.isArray(response.data.items)) {
          dataToProcess = response.data.items;
        } else if (response.data.status === 200 && response.data.data) {
          dataToProcess = response.data.data;
        } else if (Array.isArray(response.data)) {
          dataToProcess = response.data;
        } else if (response.data.htsno) {
          dataToProcess = response.data;
        }

        if (
          dataToProcess &&
          Array.isArray(dataToProcess) &&
          dataToProcess.length > 0
        ) {
          const formattedOptions = dataToProcess
            .filter((item) => item && item.htsno && item.description)
            .filter((item) => {
              // Geçersiz değerleri filtrele
              const taxRate = item.general || item.special || item.other;
              return taxRate && taxRate !== "" && taxRate !== "N/A";
            })
            .map((item) => ({
              label: `${item.htsno} - ${item.description}`,
              value: item.htsno,
              data: item,
            }));

          setHsOptions(formattedOptions);
        } else {
          setHsOptions([]);
        }
      } else {
        setHsOptions([]);
      }
    } catch (error) {
      console.error("HS kodu arama hatası:", error);
      setHsOptions([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const searchBulkHsCodes = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length <= 2) {
      setBulkHsOptions([]);
      return;
    }

    // Sadece HS kodu kısmını al (açıklama kısmını çıkar)
    const cleanSearchTerm = searchTerm.split(" - ")[0].trim();

    setBulkSearchLoading(true);
    try {
      const response = await axios.get(`hscode-search/${cleanSearchTerm}`);

      if (response.data && !response.data.error) {
        let dataToProcess = null;

        if (response.data.items && Array.isArray(response.data.items)) {
          dataToProcess = response.data.items;
        } else if (response.data.status === 200 && response.data.data) {
          dataToProcess = response.data.data;
        } else if (Array.isArray(response.data)) {
          dataToProcess = response.data;
        } else if (response.data.htsno) {
          dataToProcess = response.data;
        }

        if (
          dataToProcess &&
          Array.isArray(dataToProcess) &&
          dataToProcess.length > 0
        ) {
          const formattedOptions = dataToProcess
            .filter((item) => item && item.htsno && item.description)
            .filter((item) => {
              // Geçersiz değerleri filtrele
              const taxRate = item.general || item.special || item.other;
              return taxRate && taxRate !== "" && taxRate !== "N/A";
            })
            .map((item) => ({
              label: `${item.htsno} - ${item.description}`,
              value: item.htsno,
              data: item,
            }));

          setBulkHsOptions(formattedOptions);
        } else {
          setBulkHsOptions([]);
        }
      } else {
        setBulkHsOptions([]);
      }
    } catch (error) {
      console.error("Bulk HS kodu arama hatası:", error);
      setBulkHsOptions([]);
    } finally {
      setBulkSearchLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(
    (searchTerm) => {
      const timeoutId = setTimeout(() => {
        searchHsCodes(searchTerm);
      }, 500);
      return () => clearTimeout(timeoutId);
    },
    [searchHsCodes]
  );

  const debouncedBulkSearch = useCallback(
    (searchTerm) => {
      const timeoutId = setTimeout(() => {
        searchBulkHsCodes(searchTerm);
      }, 500);
      return () => clearTimeout(timeoutId);
    },
    [searchBulkHsCodes]
  );

  const handleBulkUpdate = () => {
    const updatedProducts = selectedFastShipmentProduct.map((product) => {
      // Bulk update için HS kodu belirle
      const newHsCode =
        bulkSelectedMemberHsCode ||
        (bulkSelectedHsOption ? bulkSelectedHsOption.value : bulkHsCode) ||
        product.hs_code;

      // Bulk update için gümrük vergisi oranı belirle
      let newDdpRate = product.ddp_rate;
      let newHsOption = product.hs_option;

      // Eğer bulk HS seçeneği varsa, onun bilgilerini kullan
      if (bulkSelectedHsOption) {
        newDdpRate = bulkDdpRate;
        newHsOption = {
          label: bulkSelectedHsOption.label,
          value: bulkSelectedHsOption.value,
          data: bulkSelectedHsOption.data,
        };
      }
      // Eğer bulk member HS kodu seçilmişse, onun bilgilerini kullan
      else if (bulkSelectedMemberHsCode) {
        newDdpRate = bulkDdpRate;
        newHsOption = bulkSelectedHsOption;
      }

      // Eğer bulk gümrük vergisi oranı varsa, onu kullan
      if (bulkDdpRate !== null && bulkDdpRate !== undefined) {
        newDdpRate = bulkDdpRate;
      }

      // Eğer bulk HS seçeneği varsa, onu kullan
      if (bulkSelectedHsOption) {
        newHsOption = bulkSelectedHsOption;
      }

      // Eğer bulk member HS kodu seçilmişse ve bulk HS seçeneği yoksa, member kodundan HS seçeneği oluştur
      if (bulkSelectedMemberHsCode && !bulkSelectedHsOption) {
        const selectedMemberCode = memberHsCodes.find(
          (code) => code.value === bulkSelectedMemberHsCode
        );
        if (selectedMemberCode) {
          newHsOption = {
            label: selectedMemberCode.value,
            value: selectedMemberCode.value,
            data: selectedMemberCode.data,
          };
        }
      }

      // Eğer bulk member HS kodu seçilmişse ve bulk HS seçeneği varsa, onun bilgilerini kullan
      if (bulkSelectedMemberHsCode && bulkSelectedHsOption) {
        newDdpRate = bulkDdpRate;
        newHsOption = bulkSelectedHsOption;
      }

      return {
        ...product,
        parcel_type: bulkParcelType || product.parcel_type,
        currency: bulkCurrency || product.currency,
        country: bulkCountry || product.country,
        title: bulkTitle || product.title,
        sku: bulkSku || product.sku,
        hs_code: newHsCode,
        quantity: bulkQuantity || product.quantity,
        price: bulkPrice || product.price,
        ddp_rate: newDdpRate,
        hs_option: newHsOption,
        ioss: bulkIoss || product.ioss,
      };
    });
    setSelectedFastShipmentProduct(updatedProducts);

    // Bulk alanları temizle
    setBulkSelectedHsOption(null);
    setBulkHsCodeSearch("");
    setBulkHsOptions([]);
    setBulkDdpRate(null);
    setBulkParcelType("");
    setBulkCurrency("");
    setBulkCountry("");
    setBulkTitle("");
    setBulkSku("");
    setBulkHsCode("");
    setBulkQuantity("");
    setBulkSelectedMemberHsCode("");
    setBulkIoss("");
  };

  const handleProductChange = useCallback(
    (e, key) => {
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, title: e.target.value } : product
        )
      );
    },
    [setSelectedFastShipmentProduct]
  );

  const handleQuantityChange = useCallback(
    (e, key) => {
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, quantity: e.target.value } : product
        )
      );
    },
    [setSelectedFastShipmentProduct]
  );

  const handlePriceChange = useCallback(
    (e, key) => {
      const value = FormatReturnFieldWithTwoDecimals(e.target.value);
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, price: value } : product
        )
      );
      setInitialPrice(product.price); // Store the initial price
    },
    [setSelectedFastShipmentProduct, product.price]
  );

  const handleCountryChange = useCallback(
    (e, key) => {
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, country: e.target.value } : product
        )
      );
    },
    [setSelectedFastShipmentProduct]
  );

  const handleSkuChange = useCallback(
    (e, key) => {
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, sku: e.target.value } : product
        )
      );
    },
    [setSelectedFastShipmentProduct]
  );

  const handleHsCodeChange = useCallback(
    (e, key) => {
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, hs_code: e.target.value } : product
        )
      );
    },
    [setSelectedFastShipmentProduct]
  );

  const handleParcelTypeChange = useCallback(
    (e, key) => {
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, parcel_type: e.target.value } : product
        )
      );
    },
    [setSelectedFastShipmentProduct]
  );

  const handleCurrencyChange = useCallback(
    (e, key) => {
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, currency: e.target.value } : product
        )
      );
    },
    [setSelectedFastShipmentProduct]
  );

  const handleIossChange = useCallback(
    (e, key) => {
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, ioss: e.target.value } : product
        )
      );
    },
    [setSelectedFastShipmentProduct]
  );

  const handleMemberHsCodeChange = useCallback(
    (e, key) => {
      const selectedValue = e.target.value;
      setSelectedMemberHsCode(selectedValue);
      setIsMemberHsCodeSelected(selectedValue !== "");

      // Seçilen member HS kodunu bul
      const selectedMemberCode = memberHsCodes.find(
        (code) => code.value === selectedValue
      );

      // Seçilen member HS kodunu product'a uygula
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, hs_code: selectedValue } : product
        )
      );

      // Eğer member HS kodu seçildiyse, Autocomplete alanını da güncelle
      if (selectedMemberCode) {
        // HS kodu açıklamasını ürün açıklaması alanına ekle
        if (selectedMemberCode.data && selectedMemberCode.data.description) {
          setSelectedFastShipmentProduct((prevProducts) =>
            prevProducts.map((product, i) =>
              i === key ? { ...product, title: selectedMemberCode.data.description } : product
            )
          );
        }

        // HS kodu için gümrük vergisi bilgisini al
        const fetchTaxRate = async () => {
          setTaxLoading(true);
          try {
            const response = await axios.get(
              `hscode-search/${selectedMemberCode.value}`
            );
            if (
              response.data &&
              !response.data.error &&
              response.data.items &&
              response.data.items.length > 0
            ) {
              // Member HS kodu seçildiğinde sadece tam eşleşen kodu bul
              const exactMatch = response.data.items.find(item => item.htsno === selectedMemberCode.value);
              const hsData = exactMatch || response.data.items[0];
              const taxRate = hsData.general || hsData.special || hsData.other;

              let numericRate = null;
              if (taxRate && taxRate !== "" && taxRate !== "N/A") {
                const rate = taxRate.toLowerCase();
                if (rate === "free" || rate === "0%" || rate === "0") {
                  numericRate = 0;
                } else if (rate.includes("%")) {
                  const parsedRate = parseFloat(rate.replace("%", ""));
                  if (!isNaN(parsedRate)) {
                    numericRate = parsedRate;
                  }
                } else if (!isNaN(parseFloat(rate))) {
                  numericRate = parseFloat(rate);
                }
              }

              // Güncellenmiş data ile HS kodu objesini oluştur
              const updatedData = {
                ...hsData,
                general: taxRate,
                special: hsData.special,
                other: hsData.other,
              };

              const hsCodeWithTax = {
                label: selectedMemberCode.value, // Sadece HS kodu
                value: selectedMemberCode.value, // Sadece HS kodu
                data: updatedData,
              };

              setSelectedHsOption(hsCodeWithTax);
              setHsOptions([hsCodeWithTax]);
              setDdpRate(numericRate);
              setHsCodeSearch(selectedValue); // Autocomplete input değerini güncelle

              // Product'ı da güncelle
              setSelectedFastShipmentProduct((prevProducts) =>
                prevProducts.map((product, i) =>
                  i === key
                    ? {
                      ...product,
                      hs_code: selectedValue,
                      ddp_rate: numericRate,
                      hs_option: hsCodeWithTax,
                    }
                    : product
                )
              );
            } else {
              // API'den veri alınamazsa sadece HS kodu ile devam et
              const hsCodeOnly = {
                label: selectedMemberCode.value,
                value: selectedMemberCode.value,
                data: selectedMemberCode.data,
              };
              setSelectedHsOption(hsCodeOnly);
              setHsOptions([hsCodeOnly]);
              setDdpRate(null);
            }
          } catch (error) {
            console.error("HS kodu vergi oranı alınamadı:", error);
            // Hata durumunda sadece HS kodu ile devam et
            const hsCodeOnly = {
              label: selectedMemberCode.value,
              value: selectedMemberCode.value,
              data: selectedMemberCode.data,
            };
            setSelectedHsOption(hsCodeOnly);
            setHsOptions([hsCodeOnly]);
            setDdpRate(null);
          } finally {
            setTaxLoading(false);
          }
        };

        fetchTaxRate();
        // Autocomplete dropdown'ını otomatik aç
        setAutocompleteOpen(true);
      } else {
        // Member HS kodu temizlendiğinde Autocomplete alanını da temizle
        setSelectedHsOption(null);
        setHsOptions([]);
        setAutocompleteOpen(false);
        setDdpRate(null);
        setHsCodeSearch(""); // Autocomplete input değerini temizle
      }
    },
    [setSelectedFastShipmentProduct, memberHsCodes]
  );

  const handleBulkMemberHsCodeChange = useCallback(
    (e) => {
      const selectedValue = e.target.value;
      setBulkSelectedMemberHsCode(selectedValue);
      setIsBulkMemberHsCodeSelected(selectedValue !== "");

      // Seçilen member HS kodunu bul
      const selectedMemberCode = memberHsCodes.find(
        (code) => code.value === selectedValue
      );

      // Eğer member HS kodu seçildiyse, Bulk Autocomplete alanını da güncelle
      if (selectedMemberCode) {
        // HS kodu açıklamasını bulk ürün açıklaması alanına ekle
        if (selectedMemberCode.data && selectedMemberCode.data.description) {
          setBulkTitle(selectedMemberCode.data.description);
        }

        // HS kodu için gümrük vergisi bilgisini al
        const fetchBulkTaxRate = async () => {
          setBulkTaxLoading(true);
          try {
            const response = await axios.get(
              `hscode-search/${selectedMemberCode.value}`
            );
            if (
              response.data &&
              !response.data.error &&
              response.data.items &&
              response.data.items.length > 0
            ) {
              // Member HS kodu seçildiğinde sadece tam eşleşen kodu bul
              const exactMatch = response.data.items.find(item => item.htsno === selectedMemberCode.value);
              const hsData = exactMatch || response.data.items[0];
              const taxRate = hsData.general || hsData.special || hsData.other;



              let numericRate = null;
              if (taxRate && taxRate !== "" && taxRate !== "N/A") {
                const rate = taxRate.toLowerCase();


                if (rate === "free" || rate === "0%" || rate === "0") {
                  numericRate = 0;

                } else if (rate.includes("%")) {
                  const parsedRate = parseFloat(rate.replace("%", ""));
                  if (!isNaN(parsedRate)) {
                    numericRate = parsedRate;

                  }
                } else if (!isNaN(parseFloat(rate))) {
                  numericRate = parseFloat(rate);

                }
              } else {

              }

              // Güncellenmiş data ile HS kodu objesini oluştur
              const updatedData = {
                ...hsData,
                general: taxRate,
                special: hsData.special,
                other: hsData.other,
              };

              const hsCodeWithTax = {
                label: selectedMemberCode.value, // Sadece HS kodu
                value: selectedMemberCode.value, // Sadece HS kodu
                data: updatedData,
              };

              setBulkSelectedHsOption(hsCodeWithTax);
              setBulkHsOptions([hsCodeWithTax]);
              setBulkDdpRate(numericRate);
              setBulkHsCodeSearch(selectedValue); // Bulk Autocomplete input değerini güncelle
              // Bulk member HS kodu seçildiğinde ürün açıklamasını da güncelle
              setBulkTitle(selectedMemberCode.data?.description || "");

              // Bulk update için gümrük vergisi bilgisini güncelle


              // Bulk update için gümrük vergisi bilgisini güncelle

            } else {
              // API'den veri alınamazsa sadece HS kodu ile devam et
              const hsCodeOnly = {
                label: selectedMemberCode.value,
                value: selectedMemberCode.value,
                data: selectedMemberCode.data,
              };
              setBulkSelectedHsOption(hsCodeOnly);
              setBulkHsOptions([hsCodeOnly]);
              setBulkDdpRate(null);
              // API'den veri alınamasa bile member HS kodunun açıklamasını kullan
              setBulkTitle(selectedMemberCode.data?.description || "");
            }
          } catch (error) {
            console.error("Bulk HS kodu vergi oranı alınamadı:", error);
            // Hata durumunda sadece HS kodu ile devam et
            const hsCodeOnly = {
              label: selectedMemberCode.value,
              value: selectedMemberCode.value,
              data: selectedMemberCode.data,
            };
            setBulkSelectedHsOption(hsCodeOnly);
            setBulkHsOptions([hsCodeOnly]);
            setBulkDdpRate(null);
            // Hata durumunda da member HS kodunun açıklamasını kullan
            setBulkTitle(selectedMemberCode.data?.description || "");
          } finally {
            setBulkTaxLoading(false);
          }
        };

        fetchBulkTaxRate();
        // Bulk Autocomplete dropdown'ını otomatik aç
        setBulkAutocompleteOpen(true);
      } else {
        // Bulk member HS kodu temizlendiğinde Autocomplete alanını da temizle
        setBulkSelectedHsOption(null);
        setBulkHsOptions([]);
        setBulkAutocompleteOpen(false);
        setBulkDdpRate(null);
        setBulkHsCodeSearch(""); // Bulk Autocomplete input değerini temizle
        setBulkTitle("");
      }
    },
    [memberHsCodes]
  );

  const handleIOSSCreate = useCallback(() => {
    setProformaModal(true);
    setPlatformCountry(IOSSCountry);
    setNewIossNumberAnimation(true);
  }, [
    IOSSCountry,
    setProformaModal,
    setNewIossNumberAnimation,
    setPlatformCountry,
  ]);

  const handleEditSubmit = async () => {
    setFileChange(true);
    if (!hsCodeError) {
      setLoading(true);

      const formData = new FormData();

      selectedFastShipmentProduct.forEach((product, productIndex) => {
        formData.append(
          `commoduties[${productIndex}][commoduty_id]`,
          product.id
        );
        formData.append(
          `commoduties[${productIndex}][integration_parcel_id]`,
          product.integration_parcel_id
        );
        formData.append(`commoduties[${productIndex}][title]`, product.title);
        formData.append(
          `commoduties[${productIndex}][sku]`,
          product.sku && product.sku !== "null" ? product.sku : ""
        );
        formData.append(
          `commoduties[${productIndex}][parcel_type]`,
          selectedFastShipmentProduct[0]?.parcel_type ?? 1
        );
        formData.append(
          `commoduties[${productIndex}][country]`,
          product.country || "TR"
        );
        formData.append(
          `commoduties[${productIndex}][quantity]`,
          product.quantity
        );
        formData.append(
          `commoduties[${productIndex}][hs_code]`,
          product.hs_code ? product.hs_code.replace(/\./g, "") : ""
        );
        formData.append(`commoduties[${productIndex}][price]`, product.price);
        formData.append(
          `commoduties[${productIndex}][currency]`,
          selectedFastShipmentProduct[0]?.currency
        );
        formData.append(
          `commoduties[${productIndex}][total_price]`,
          Number(product.quantity) * Number(product.price)
        );
        formData.append(
          `commoduties[${productIndex}][ddp_rate]`,
          product.ddp_rate !== null && product.ddp_rate !== undefined
            ? Number(product.ddp_rate)
            : 0
        );
        formData.append(
          `commoduties[${productIndex}][ioss]`,
          selectedFastShipmentProduct[0]?.ioss &&
            selectedFastShipmentProduct[0].ioss !== "null"
            ? selectedFastShipmentProduct[0].ioss
            : " "
        );

        if (productIndex === 0) {
          sendFiles.forEach((file, fileIndex) => {
            formData.append(
              `commoduties[${productIndex}][files][${fileIndex}]`,
              file.file
            );
          });
        }
      });

      try {
        const response = await axios.post(
          `general/update-commoduty`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        toast.success(response.data.message);

        const totalPriceChanged = calculateTotalPrice() !== initialTotalPrice;
        if (!priceIsDisabled && totalPriceChanged) {
          handleCheckPrice();
          setSelectedPriceObject("");
          setPriceID("");
          setPriceIDValue("");
        }
        setUpdatedCommoduty(response.data.data.updated_commoduty);
        // Extract the updated items from the response
        const updatedItems = response.data.data;

        // Update the get_dimensions field of the corresponding items in the fastList
        // fastList'i güncelliyoruz
        setFastList((prevList) =>
          prevList.map((item) => {
            const updatedItem = updatedItems.find(
              (updated) => updated.id === item.id
            );
            if (updatedItem) {
              return {
                ...item,
                updated_commoduty: updatedItem.updated_commoduty,
                get_commoduties: updatedItem.get_commoduties,
                get_integration_parcel_documents:
                  updatedItem.get_integration_parcel_documents,
                parcel_type: updatedItem.parcel_type,
              };
            }
            return item;
          })
        );
        setProformaChange(true);
        setProforma(true);
      } catch (error) {
        Errors(error);
      } finally {
        setLoading(false);
        handleClose();
      }
    }
  };

  const message = () => {
    if (hasAnyProductWithMissingTaxRate()) {
      return "Gümrük vergisi bilgisi eksik olan ürünler var. Lütfen geçerli bir HS kodu seçin.";
    } else if (hsCodeError && !allCurrenciesSame) {
      return "Hs Kodu 6 haneden küçük girilemez ve tüm döviz cinsleri aynı olmalıdır.";
    } else if (hsCodeError) {
      return "Hs Kodu 6 haneden küçük girilemez.";
    } else if (!allCurrenciesSame) {
      return "Tüm döviz cinsleri aynı olmalıdır.";
    }
  };

  const handlePriceBlur = useCallback(
    (e, key) => {
      const value = parseFloat(e.target.value).toFixed(2);
      setSelectedFastShipmentProduct((prevProducts) =>
        prevProducts.map((product, i) =>
          i === key ? { ...product, price: value } : product
        )
      );
    },
    [setSelectedFastShipmentProduct]
  );

  useEffect(() => {
    // Calculate and set the initial total price when the component mounts
    setTotalPriceCalc(calculateTotalPrice());
  }, [setTotalPriceCalc, calculateTotalPrice]);

  // Component mount olduğunda member HS kodlarını yükle
  useEffect(() => {
    fetchMemberHsCodes();
  }, [fetchMemberHsCodes]);

  // Member HS kodları yüklendiğinde, eğer product.hs_code varsa ve member kodları arasındaysa selectedHsOption'ı güncelle
  useEffect(() => {
    if (memberHsCodes.length > 0 && product.hs_code) {
      const matchingMemberCode = memberHsCodes.find(
        (code) => code.value === product.hs_code
      );
      if (matchingMemberCode) {
        setSelectedHsOption(matchingMemberCode);
        setHsOptions([matchingMemberCode]);
      }
    }
  }, [memberHsCodes, product.hs_code]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="package-inputs">
        {isFirstItem && (
          <>
            <Box
              sx={{
                fontSize: "20px",
                fontWeight: "500",
                paddingLeft: "5px",
                marginBottom: "20px",
              }}
            >
              Proforma Toplu Düzenleme Ekranı
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "20px",
                marginBottom: 0,
                backgroundColor: "rgba(255, 192, 203, 0.18)",
                padding: "16px",
              }}
              className="package-inputs-first"
            >
              <FormControl sx={{ width: "50%" }}>
                <InputLabel id="bulk-select-product-description">
                  Gönderi Açıklaması
                </InputLabel>
                <Select
                  labelId="bulk-select-product-description"
                  id="bulk-select-simple-product-description"
                  value={bulkParcelType}
                  onChange={(e) => setBulkParcelType(e.target.value)}
                  label="Gönderi Açıklaması"
                  name="bulk_parcel_type"
                >
                  <MenuItem value="">
                    <em>Temizle</em>
                  </MenuItem>
                  <MenuItem value={1}>Normal Gönderi veya Numune</MenuItem>
                  <MenuItem value={2}>Hediye</MenuItem>
                  {/* <MenuItem value={3}>
                    Mikro İhracat (ETGB'li) E-ihracat
                  </MenuItem> */}
                </Select>
              </FormControl>
              <FormControl sx={{ width: "50%" }}>
                <InputLabel id="bulk-select-currency">Döviz</InputLabel>
                <Select
                  labelId="bulk-select-currency"
                  id="bulk-select-simple-currency"
                  value={bulkCurrency}
                  onChange={(e) => setBulkCurrency(e.target.value)}
                  label="Döviz"
                  name="bulk_currency"
                >
                  <MenuItem value="">
                    <em>Temizle</em>
                  </MenuItem>
                  <MenuItem value={"EUR"}>€</MenuItem>
                  <MenuItem value={"USD"}>$</MenuItem>
                  <MenuItem value={"GBP"}>£</MenuItem>
                </Select>
              </FormControl>
              {IOSSCountry && (
                <FormControl
                  sx={{
                    width: "50%",
                    height: "40px",
                  }}
                >
                  <InputLabel id="select-ioss" shrink={Boolean(bulkIoss)}>
                    IOSS/VAT
                  </InputLabel>
                  <Select
                    labelId="select-ioss"
                    id="select-simple-ioss"
                    value={bulkIoss || ""}
                    onChange={(e) => setBulkIoss(e.target.value)}
                    label="IOSS/VAT"
                    sx={{ height: "52px" }}
                  >
                    <MenuItem value="">
                      <em>Temizle</em>
                    </MenuItem>
                    <MenuItem
                      sx={{
                        color: "var(--btnBgBlue)",
                        textDecoration: "underline",
                      }}
                      value={""}
                      onClick={handleIOSSCreate}
                    >
                      <em>IOSS Kaydı Oluştur</em>
                    </MenuItem>
                    {integrations?.map((integration) => (
                      <MenuItem
                        sx={{
                          display: "flex",
                          gap: "var(--gap)",
                          alignItems: "center",
                        }}
                        key={integration.id}
                        value={integration.ioss_number}
                      >
                        <img
                          src={`/images/integrations/${integration.market_place}.svg`}
                          style={{
                            width: "50px",
                            height: "20px",
                            objectFit: "contain",
                            objectPosition: "center",
                          }}
                          alt={`${integration.market_place} logo`}
                        />{" "}
                        {integration.ioss_number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "20px",
                justifyContent: "space-between",
                backgroundColor: "rgba(255, 192, 203, 0.18)",
                padding: "16px",
              }}
              className="package-inputs-second"
            >
              <Select
                value={bulkCountry}
                onChange={(e) => setBulkCountry(e.target.value)}
                sx={{ height: "54px", width: "50%" }}
                displayEmpty
                renderValue={(selected) => {
                  if (selected === "") {
                    return "Ülke Seçiniz";
                  }
                  const country = countries.find(
                    (country) => country.code === selected
                  );
                  return (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <img
                        loading="lazy"
                        style={{ marginRight: "5px", width: "20px" }}
                        src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                        alt={`${country.label} Milli Bayrağı Görseli`}
                      />
                      {country.label}
                    </Box>
                  );
                }}
              >
                <MenuItem value="">Temizle</MenuItem>
                {countries.map((country, key) => (
                  <MenuItem key={key} value={country.code}>
                    <img
                      loading="lazy"
                      style={{ marginRight: "5px", width: "20px" }}
                      src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                      srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                      alt={`${country.label} Milli Bayrağı Görseli`}
                    />
                    {country.label}
                  </MenuItem>
                ))}
              </Select>

              <TextField
                value={bulkTitle}
                onChange={(e) => setBulkTitle(e.target.value)}
                name="title"
                label="Ürün Açıklaması"
                autoComplete="off"
                sx={{ width: "80%" }}
              />
              <TextField
                value={bulkSku}
                onChange={(e) => setBulkSku(e.target.value)}
                name="sku"
                label="SKU"
                autoComplete="off"
                sx={{ width: "50%" }}
              />
              {itemID?.country === "US" && (
                <div
                  style={{
                    width: "50%",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  {/* Kayıtlı HS Kodları Seçim Kutusu */}
                  <FormControl sx={{ width: "400px", marginRight: "10px" }}>
                    <InputLabel id="bulk-member-hs-codes">
                      Kayıtlı HS Kodları
                    </InputLabel>
                    <Select
                      labelId="bulk-member-hs-codes"
                      id="bulk-member-hs-codes-select"
                      value={bulkSelectedMemberHsCode}
                      onChange={handleBulkMemberHsCodeChange}
                      label="Kayıtlı HS Kodları"
                      disabled={memberHsLoading}
                      sx={{ minHeight: "54px" }}
                    >
                      <MenuItem value="">
                        <em>Temizle</em>
                      </MenuItem>
                      {memberHsCodes.map((memberCode) => (
                        <MenuItem
                          key={memberCode.memberId}
                          value={memberCode.value}
                        >
                          {memberCode.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* HS Kodu veya Ürün Adı Autocomplete */}
                  <div style={{ flex: 1 }}>
                    <Autocomplete
                      fullWidth
                      sx={{ height: "54px", width: "300px" }}
                      options={bulkHsOptions}
                      open={bulkAutocompleteOpen}
                      onOpen={() => setBulkAutocompleteOpen(true)}
                      onClose={() => setBulkAutocompleteOpen(false)}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return option?.label || option?.value || "";
                      }}
                      isOptionEqualToValue={(option, value) => {
                        return option?.value === value?.value;
                      }}
                      value={bulkSelectedHsOption}
                      onChange={(event, newValue) => {
                        setBulkSelectedHsOption(newValue);

                        // HS Code seçildiğinde vergi oranını kaydet
                        if (newValue && newValue.data) {
                          const taxRate =
                            newValue.data.general ||
                            newValue.data.special ||
                            newValue.data.other;

                          let numericRate = null;
                          if (taxRate && taxRate !== "" && taxRate !== "N/A") {
                            const rate = taxRate.toLowerCase();
                            if (
                              rate === "free" ||
                              rate === "0%" ||
                              rate === "0"
                            ) {
                              numericRate = 0;
                            } else if (rate.includes("%")) {
                              const parsedRate = parseFloat(
                                rate.replace("%", "")
                              );
                              if (!isNaN(parsedRate)) {
                                numericRate = parsedRate;
                              }
                            } else if (!isNaN(parseFloat(rate))) {
                              numericRate = parseFloat(rate);
                            }
                          }
                          setBulkDdpRate(numericRate);
                          setBulkHsCode(newValue.value);
                        } else {
                          setBulkDdpRate(null);
                          setBulkHsCode("");
                        }
                      }}
                      onInputChange={(event, newInputValue) => {
                        setBulkHsCodeSearch(newInputValue);
                        // Bulk member HS kodu seçildiyse arama yapma
                        if (!isBulkMemberHsCodeSelected) {
                          debouncedBulkSearch(newInputValue);
                        }
                      }}
                      loading={bulkSearchLoading}
                      loadingText="Aranıyor..."
                      noOptionsText="Sonuç bulunamadı"
                      filterOptions={(options) => options}
                      size="small"
                      renderOption={(props, option) => (
                        <li {...props} key={option.value}>
                          <div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <strong>{option.data?.htsno} </strong>
                              <div style={{ whiteSpace: "nowrap" }}>
                                <span
                                  style={{ fontSize: "14px", color: "#666" }}
                                >
                                  {" "}
                                  Gümrük vergisi:{" "}
                                </span>
                                <span
                                  style={{
                                    color: "#d32f2f",
                                    fontWeight: "bold",
                                    fontSize: "0.9em",
                                  }}
                                >
                                  {option.data?.general ||
                                    option.data?.special ||
                                    option.data?.other ||
                                    "0"}
                                </span>
                              </div>
                            </div>
                            <span style={{ fontSize: "0.9em", color: "#666" }}>
                              {option.data?.description}
                            </span>
                          </div>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="HS Kodu veya Ürün Adı (İngilizce)"
                          placeholder="HS Kodu veya Ürün Adı (İngilizce)"
                          variant="outlined"
                          size="medium"
                          sx={{ height: "54px" }}
                          helperText={
                            <>
                              {hsCodeError &&
                                "Hs Kodu 6 haneden küçük girilemez."}
                              {!hsCodeError && (
                                <a
                                  rel="noreferrer"
                                  href="https://uygulama.gtb.gov.tr/Tara/"
                                  target="_blank"
                                  tabIndex={-1}
                                  style={{
                                    fontSize: "inherit",
                                    color: "var(--btnBgBlue)",
                                  }}
                                >
                                  ABD gönderileri için HS Kodu zorunludur. GTIP
                                  - HSCODE sorgula
                                </a>
                              )}
                            </>
                          }
                          InputProps={{
                            ...params.InputProps,
                            style: { height: "54px" },
                            endAdornment: (
                              <>
                                {bulkSearchLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </div>

                  {/* Vergi oranı HS Code'un yanında */}
                  {bulkSelectedHsOption && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                        minHeight: "54px",
                        minWidth: "150px",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        <span style={{ fontSize: "14px", color: "#666" }}>
                          {""}Gümrük vergisi:{" "}
                        </span>
                        <span
                          style={{
                            color: "#d32f2f",
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          {bulkDdpRate !== null && bulkDdpRate !== undefined
                            ? `${bulkDdpRate}%`
                            : bulkSelectedHsOption.data?.general ||
                            bulkSelectedHsOption.data?.special ||
                            bulkSelectedHsOption.data?.other ||
                            "0%"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {itemID?.country !== "US" && (
                <TextField
                  value={bulkHsCode || ""}
                  onChange={(e) => setBulkHsCode(e.target.value)}
                  label="HS Kodu"
                  name="hs_code"
                  sx={{ width: "50%" }}
                  helperText={
                    <>
                      {hsCodeError && "Hs Kodu 6 haneden küçük girilemez."}
                      {!hsCodeError && (
                        <a
                          rel="noreferrer"
                          href="https://uygulama.gtb.gov.tr/Tara/"
                          target="_blank"
                          tabIndex={-1}
                          style={{
                            fontSize: "inherit",
                            color: "var(--btnBgBlue)",
                          }}
                        >
                          ABD gönderileri için HS Kodu zorunludur. GTIP - HSCODE
                          sorgula
                        </a>
                      )}
                    </>
                  }
                />
              )}

              <TextField
                sx={{ width: "30%" }}
                value={bulkQuantity}
                onChange={(e) => setBulkQuantity(e.target.value)}
                name="qty"
                label="Miktar"
                //disabled={(itemID?.country === "US")}
                autoComplete="off"
                inputProps={{
                  min: 1, // En küçük değer sıfır olmalıdır
                }}
                error={bulkQuantity < 1 && bulkQuantity.length !== 0}
                helperText={
                  bulkQuantity < 1 &&
                  bulkQuantity.length !== 0 &&
                  "Miktar 1'dan küçük olamaz."
                }
              />
              <TextField
                sx={{ width: "30%" }}
                value={bulkPrice}
                onChange={(e) => setBulkPrice(e.target.value)}
                name="price"
                label="Birim Fiyatı"
                //disabled={(itemID?.country === "US")}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment>
                      {Currency(selectedFastShipmentProduct[0]?.currency)}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 0,
                marginBottom: "30px",
                backgroundColor: "rgba(255, 192, 203, 0.18)",
                padding: "12px 16px",
              }}
            >
              <Button onClick={handleBulkUpdate} className="iyzi-button-blue">
                Toplu Güncelle
              </Button>
            </Box>
          </>
        )}
        {/* Single Product Inputs */}
        {isFirstItem && (
          <>
            <Box
              sx={{
                fontSize: "20px",
                fontWeight: "500",
                paddingLeft: "5px",
                marginBottom: "20px",
              }}
            >
              Entegrasyondan Gelen Proforma Ekranı
            </Box>
          </>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            marginTop: 0,
            backgroundColor: "rgba(173, 216, 230, 0.18)",
            //padding: "16px",
          }}>
          {isFirstItem && (
            <Box
              sx={{
                display: "flex",
                gap: "20px",
                marginTop: "20px",
                marginBottom: "20px",
                padding: "16px",
                borderRadius: "8px",
              }}
              className="package-inputs-first"
            >
              <FormControl sx={{ width: "50%" }}>
                <InputLabel id="select-product-description">
                  Gönderi Açıklaması
                </InputLabel>
                <Select
                  labelId="select-product-description"
                  id="select-simple-product-description"
                  value={selectedFastShipmentProduct[0]?.parcel_type ?? 1}
                  required
                  onChange={(e) => handleParcelTypeChange(e, index)}
                  label="Gönderi Açıklaması"
                  name="parcel_type"
                >
                  <MenuItem value="">
                    <em>Temizle</em>
                  </MenuItem>
                  <MenuItem value={1}>Normal Gönderi veya Numune</MenuItem>
                  <MenuItem value={2}>Hediye</MenuItem>
                  {/* <MenuItem value={3}>
                    Mikro İhracat (ETGB'li) E-ihracat
                  </MenuItem> */}
                </Select>
              </FormControl>
              <FormControl sx={{ width: "50%" }}>
                <InputLabel id="select-currency">Döviz</InputLabel>
                <Select
                  labelId="select-currency"
                  id="select-simple-currency"
                  value={selectedFastShipmentProduct[0]?.currency}
                  onChange={(e) => handleCurrencyChange(e, index)}
                  label="Döviz"
                  name="currency"
                >
                  <MenuItem value="">
                    <em>Temizle</em>
                  </MenuItem>
                  <MenuItem value={"EUR"}>€</MenuItem>
                  <MenuItem value={"USD"}>$</MenuItem>
                  <MenuItem value={"GBP"}>£</MenuItem>
                </Select>
              </FormControl>
              {IOSSCountry && (
                <FormControl
                  sx={{
                    width: "50%",
                    height: "40px",
                  }}
                >
                  <InputLabel id="select-ioss" shrink={Boolean(product.ioss)}>
                    IOSS/VAT
                  </InputLabel>
                  <Select
                    labelId="select-ioss"
                    id="select-simple-ioss"
                    value={product.ioss || ""}
                    onChange={(e) => handleIossChange(e, index)}
                    label="IOSS/VAT"
                    sx={{ height: "52px" }}
                  >
                    <MenuItem value="">
                      <em>Temizle</em>
                    </MenuItem>
                    <MenuItem
                      sx={{
                        color: "var(--btnBgBlue)",
                        textDecoration: "underline",
                      }}
                      value={""}
                      onClick={handleIOSSCreate}
                    >
                      <em>IOSS Kaydı Oluştur</em>
                    </MenuItem>
                    {integrations?.map((integration) => (
                      <MenuItem
                        sx={{
                          display: "flex",
                          gap: "var(--gap)",
                          alignItems: "center",
                        }}
                        key={integration.id}
                        value={integration.ioss_number}
                      >
                        <img
                          src={`/images/integrations/${integration.market_place}.svg`}
                          style={{
                            width: "50px",
                            height: "20px",
                            objectFit: "contain",
                            objectPosition: "center",
                          }}
                          alt={`${integration.market_place} logo`}
                        />{" "}
                        {integration.ioss_number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              gap: "20px",
              justifyContent: "space-between",
              marginTop: 0,
              marginBottom: "20px",
              padding: "16px",
            }}
            className="package-inputs-second"
          >
            <Select
              value={product.country || "TR"}
              onChange={(e) => handleCountryChange(e, index)}
              sx={{ height: "54px", width: "50%" }}
            >
              <MenuItem value="">
                <em>Temizle</em>
              </MenuItem>
              {countries.map((country, key) => (
                <MenuItem key={key} value={country.code}>
                  <img
                    loading="lazy"
                    style={{ marginRight: "5px", width: "20px" }}
                    src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                    alt={`${country.label} Milli Bayrağı Görseli`}
                  />
                  {country.label}
                </MenuItem>
              ))}
            </Select>
            <TextField
              value={product.title}
              required
              onChange={(e) => handleProductChange(e, index)}
              name="title"
              label="Ürün Açıklaması"
              autoComplete="off"
              sx={{ width: "80%" }}
            />
            <TextField
              value={product.sku}
              onChange={(e) => handleSkuChange(e, index)}
              name="sku"
              label="SKU"
              autoComplete="off"
              sx={{ width: "50%" }}
            />
            {itemID?.country !== "US" && (
              <TextField
                value={product.hs_code}
                onChange={(e) => handleHsCodeChange(e, index)}
                name="hs_code"
                label="HS Kodu"
                type="number"
                autoComplete="off"
                sx={{ width: "50%" }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                InputProps={{
                  sx: {
                    "& input[type=number]": {
                      "-moz-appearance": "textfield",
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                  },
                }}
                helperText={
                  <>
                    {hsCodeError && "Hs Kodu 6 haneden küçük girilemez."}
                    {!hsCodeError && (
                      <a
                        rel="noreferrer"
                        href="https://uygulama.gtb.gov.tr/Tara/"
                        target="_blank"
                        tabIndex={-1}
                        style={{
                          fontSize: "inherit",
                          color: "var(--btnBgBlue)",
                        }}
                      >
                        GTIP - HSCODE sorgula
                      </a>
                    )}
                  </>
                }
              />
            )}
            {itemID?.country === "US" && (
              <div
                style={{
                  width: "50%",
                  height: "54px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  marginBottom: "25px",
                }}
              >
                {/* Kayıtlı HS Kodları Seçim Kutusu */}
                <FormControl sx={{ width: "400px", marginRight: "10px" }}>
                  <InputLabel id="member-hs-codes">Kayıtlı HS Kodları</InputLabel>
                  <Select
                    labelId="member-hs-codes"
                    id="member-hs-codes-select"
                    value={selectedMemberHsCode}
                    onChange={(e) => handleMemberHsCodeChange(e, index)}
                    label="Kayıtlı HS Kodları"
                    disabled={memberHsLoading}
                    sx={{ minHeight: "54px" }}
                  >
                    <MenuItem value="">
                      <em>Temizle</em>
                    </MenuItem>
                    {memberHsCodes.map((memberCode) => (
                      <MenuItem
                        key={memberCode.memberId}
                        value={memberCode.value}
                      >
                        {memberCode.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* HS Kodu veya Ürün Adı Autocomplete */}
                <div style={{ flex: 1 }}>
                  <Autocomplete
                    fullWidth
                    sx={{ height: "54px", width: "300px" }}
                    options={hsOptions}
                    open={autocompleteOpen}
                    onOpen={() => setAutocompleteOpen(true)}
                    onClose={() => setAutocompleteOpen(false)}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return option?.label || option?.value || "";
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return option?.value === value?.value;
                    }}
                    value={selectedHsOption}
                    onChange={(event, newValue) => {
                      setSelectedHsOption(newValue);

                      // HS Code seçildiğinde product'ı güncelle
                      if (newValue && newValue.data) {
                        const taxRate =
                          newValue.data.general ||
                          newValue.data.special ||
                          newValue.data.other;

                        let numericRate = null;
                        if (taxRate && taxRate !== "" && taxRate !== "N/A") {
                          const rate = taxRate.toLowerCase();
                          if (rate === "free" || rate === "0%" || rate === "0") {
                            numericRate = 0;
                          } else if (rate.includes("%")) {
                            const parsedRate = parseFloat(rate.replace("%", ""));
                            if (!isNaN(parsedRate)) {
                              numericRate = parsedRate;
                            }
                          } else if (!isNaN(parseFloat(rate))) {
                            numericRate = parseFloat(rate);
                          }
                        }
                        setDdpRate(numericRate);

                        // Product'ı güncelle - HS kodu seçildiğinde ürün açıklamasını güncelleme
                        setSelectedFastShipmentProduct((prevProducts) =>
                          prevProducts.map((prod, i) =>
                            i === index
                              ? {
                                ...prod,
                                hs_code: newValue.value,
                                ddp_rate: numericRate,
                                hs_option: newValue,
                              }
                              : prod
                          )
                        );
                      } else {
                        setDdpRate(null);
                        setSelectedFastShipmentProduct((prevProducts) =>
                          prevProducts.map((prod, i) =>
                            i === index
                              ? {
                                ...prod,
                                hs_code: "",
                                ddp_rate: null,
                                hs_option: null,
                              }
                              : prod
                          )
                        );
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      setHsCodeSearch(newInputValue);
                      // Member HS kodu seçildiyse arama yapma
                      if (!isMemberHsCodeSelected) {
                        debouncedSearch(newInputValue);
                      }
                    }}
                    loading={searchLoading}
                    loadingText="Aranıyor..."
                    noOptionsText="Sonuç bulunamadı"
                    filterOptions={(options) => options}
                    size="small"
                    renderOption={(props, option) => (
                      <li {...props} key={option.value}>
                        <div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <strong>{option.data?.htsno} </strong>
                            <div style={{ whiteSpace: "nowrap" }}>
                              <span style={{ fontSize: "14px", color: "#666" }}>
                                {" "}
                                Gümrük vergisi:{" "}
                              </span>
                              <span
                                style={{
                                  color: "#d32f2f",
                                  fontWeight: "bold",
                                  fontSize: "0.9em",
                                }}
                              >
                                {option.data?.general ||
                                  option.data?.special ||
                                  option.data?.other ||
                                  product.ddp_rate ||
                                  "0"}
                              </span>
                            </div>
                          </div>
                          <span style={{ fontSize: "0.9em", color: "#666" }}>
                            {option.data?.description}
                          </span>
                        </div>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="HS Kodu veya Ürün Adı (İngilizce)"
                        placeholder="HS Kodu veya Ürün Adı (İngilizce)"
                        variant="outlined"
                        size="medium"
                        required
                        sx={{ height: "54px" }}
                        helperText={
                          <>
                            {hsCodeError && "Hs Kodu 6 haneden küçük girilemez."}
                            {!hsCodeError && (
                              <a
                                rel="noreferrer"
                                href="https://uygulama.gtb.gov.tr/Tara/"
                                target="_blank"
                                tabIndex={-1}
                                style={{
                                  fontSize: "inherit",
                                  color: "var(--btnBgBlue)",
                                }}
                              >
                                ABD gönderileri için HS Kodu zorunludur. GTIP -
                                HSCODE sorgula
                              </a>
                            )}
                          </>
                        }
                        InputProps={{
                          ...params.InputProps,
                          style: { height: "54px" },
                          endAdornment: (
                            <>
                              {searchLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </div>

                {/* Vergi oranı HS Code'un yanında */}
                {selectedHsOption && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 12px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      minHeight: "54px",
                      minWidth: "150px",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        textAlign: "center",
                      }}
                    >
                      <span style={{ fontSize: "14px", color: "#666" }}>
                        {""}Gümrük vergisi:{" "}
                      </span>
                      <span
                        style={{
                          color: "#d32f2f",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}
                      >
                        {ddpRate !== null && ddpRate !== undefined
                          ? `${ddpRate}%`
                          : selectedHsOption.data?.general ||
                          selectedHsOption.data?.special ||
                          selectedHsOption.data?.other ||
                          "0%"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <TextField
              sx={{ width: "30%" }}
              value={product.quantity}
              required
              onChange={(e) => handleQuantityChange(e, index)}
              name="qty"
              label="Miktar"
              autoComplete="off"
              //disabled={(itemID?.country === "US")}
              inputProps={{
                min: 1, // En küçük değer sıfır olmalıdır
              }}
              error={product.quantity < 1 && product.quantity.length !== 0}
              helperText={
                product.quantity < 1 &&
                product.quantity.length !== 0 &&
                "Miktar 1'dan küçük olamaz."
              }
            />
            <TextField
              sx={{ width: "30%" }}
              value={product.price}
              required
              onChange={(e) => handlePriceChange(e, index)}
              onBlur={(e) => handlePriceBlur(e, index)}
              name="price"
              label="Birim Fiyatı"
              autoComplete="off"
              //disabled={(itemID?.country === "US")}
              InputProps={{
                startAdornment: (
                  <InputAdornment>
                    {Currency(selectedFastShipmentProduct[0]?.currency)}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
        {isLastItem && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <div
                style={{
                  backgroundColor: "var(--background)",
                  boxShadow: "var(--shadow)",
                  padding: "10px",
                  borderRadius: "var(--inputBorderRadius)",
                  color: "var(--priceText)",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                Toplam Fiyat:{" "}
                {Currency(selectedFastShipmentProduct[0]?.currency)}
                {LocalPrice(calculateTotalPrice())}{" "}
              </div>
            </div>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                alignItems: "center",
                marginTop: "10px",
              }}
            >
              <Button
                type="button"
                onClick={handleClose}
                className="iyzi-button-blue"
              >
                İptal
              </Button>
              <Tooltip title={message()}>
                <span>
                  <Button
                    disabled={
                      hsCodeError ||
                      product?.hs_code?.length < 8 ||
                      taxLoading ||
                      bulkTaxLoading ||
                      // hasAnyProductWithMissingTaxRate() ||
                      (Number(selectedFastShipmentProduct[0]?.parcel_type) === 3
                        ? itemID?.get_integration_parcel_documents <= 0 &&
                        sendFiles <= 0
                        : false)
                    }
                    sx={{ ":disabled": { opacity: 0.3 } }}
                    className="iyzi-button-blue"
                    onClick={handleEditSubmit}
                  >
                    Kaydet
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {Number(selectedFastShipmentProduct[0]?.parcel_type) === 3 && (
              <Box>
                <p>
                  Yüklenen Dosyalar <br />
                  <br />
                </p>
                {itemID?.get_integration_parcel_documents
                  ?.filter(
                    (document, index, self) =>
                      index === self.findIndex((d) => d.name === document.name)
                  )
                  .map((document, index) => (
                    <Box
                      onClick={() => GetFile(document)}
                      key={index}
                      sx={{ marginBottom: 2 }}
                    >
                      <p>
                        <strong>Ad:</strong>
                        <button
                          type="button"
                          onClick={() => GetFile(document)}
                          style={{
                            color: "gray",
                            textDecoration: "none",
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            padding: 0,
                            font: "inherit",
                          }}
                        >
                          {document.name}
                        </button>
                      </p>
                      <p>
                        <strong>Yüklenme Tarihi:</strong>{" "}
                        {FormatDateHour(document.created_at)}
                      </p>
                    </Box>
                  ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default PackageListItem;
